
import { useState, useRef } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Pause, Clock, Heart, HeartOff, Users, Volume2, VolumeX } from "lucide-react";
import { motion } from "framer-motion";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";

interface MeditationCardProps {
  id: string;
  title: string;
  description: string;
  duration: string;
  imageUrl: string;
  audioUrl?: string;
  favorite?: boolean;
  popularity?: number;
}

const MeditationCard = ({
  id,
  title,
  description,
  duration,
  imageUrl,
  audioUrl,
  favorite = false,
  popularity = 0
}: MeditationCardProps) => {
  const [isFavorite, setIsFavorite] = useState(favorite);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) {
      toast({
        title: "Áudio não disponível",
        description: "O áudio desta meditação não está disponível no momento.",
        variant: "destructive"
      });
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleEnded = async () => {
    setIsPlaying(false);
    setProgress(0);
    
    // Record completed meditation completion (simplified)
    toast({
      title: "Meditação concluída",
      description: "Parabéns por completar esta meditação!",
      variant: "default"
    });
  };

  return (
    <Card className="overflow-hidden h-full">
      <div className="relative h-48">
        <OptimizedImage
          src={imageUrl}
          alt={`Meditação: ${title} - ${description}`}
          className="w-full h-full"
          objectFit="cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        <div className="absolute top-2 right-2 flex flex-col gap-2">
          <motion.button
            className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/20"
            whileTap={{ scale: 0.9 }}
            onClick={toggleFavorite}
            aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          >
            {isFavorite ? (
              <Heart className="w-4 h-4 fill-red-500 stroke-red-500" />
            ) : (
              <HeartOff className="w-4 h-4" />
            )}
          </motion.button>
          
          {popularity > 0 && (
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/20" 
                 title={`${popularity} pessoas praticaram esta meditação`}>
              <div className="flex items-center">
                <Users className="w-3 h-3 mr-1" />
                <span className="text-xs font-semibold">{Math.floor(popularity / 10)}</span>
              </div>
            </div>
          )}
        </div>
        
        <div className="absolute bottom-2 left-3 flex items-center">
          <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-2 py-1 text-white text-xs border border-white/20">
            <Clock className="w-3 h-3 mr-1" />
            <span>{duration}</span>
          </div>
        </div>
      </div>
      
      <CardContent className="pt-4">
        <h3 className="font-semibold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>

        {audioUrl && (
          <div className="mb-4">
            <audio 
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              className="hidden"
            />
            {isPlaying && (
              <div className="mb-2">
                <Progress value={progress} className="h-1.5" />
              </div>
            )}
            <div className="flex justify-between items-center">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={toggleMute}
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
        
        <Button 
          className="w-full bg-brand-blue text-white hover:bg-opacity-90 group"
          onClick={togglePlay}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
          ) : (
            <Play className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
          )}
          {isPlaying ? "Pausar" : "Iniciar"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default MeditationCard;
